<script setup>
import { ref, computed, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(['onSelect']);

const { t } = useI18n();
const store = useStore();
const query = ref('');
const selectedLanguage = ref('');
const selectedCategory = ref('');
const selectedTemplate = ref(null);
const isRefreshing = ref(false);
const pickerSteps = { choose: 1, preview: 2 };

const whatsAppTemplateMessages = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  toRef(props, 'inboxId')
);

const getTemplateComponent = (template, type) =>
  findComponentByType(template, type);

const getTemplateBody = template =>
  getTemplateComponent(template, COMPONENT_TYPES.BODY)?.text || '';

const getTemplateHeader = template =>
  getTemplateComponent(template, COMPONENT_TYPES.HEADER);

const getTemplateFooter = template =>
  getTemplateComponent(template, COMPONENT_TYPES.FOOTER)?.text || '';

const getTemplateButtons = template =>
  getTemplateComponent(template, COMPONENT_TYPES.BUTTONS)?.buttons || [];

const hasMediaContent = template => {
  const header = getTemplateHeader(template);
  return header && MEDIA_FORMATS.includes(header.format);
};

const normalizeSearchText = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const getTemplateSearchText = template => {
  const componentText = (template.components || []).flatMap(component => [
    component.text,
    ...(component.buttons || []).map(button => button.text),
  ]);

  return normalizeSearchText(
    [template.name, template.language, template.category, ...componentText]
      .filter(Boolean)
      .join(' ')
  );
};

const formatTemplateName = name => {
  const formattedName = String(name || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return formattedName
    ? formattedName.charAt(0).toUpperCase() + formattedName.slice(1)
    : t('WHATSAPP_TEMPLATES.PICKER.UNNAMED_TEMPLATE');
};

const getCategoryLabel = category => {
  const categoryLabels = {
    MARKETING: t('WHATSAPP_TEMPLATES.PICKER.CATEGORY_LABELS.MARKETING'),
    UTILITY: t('WHATSAPP_TEMPLATES.PICKER.CATEGORY_LABELS.UTILITY'),
    AUTHENTICATION: t(
      'WHATSAPP_TEMPLATES.PICKER.CATEGORY_LABELS.AUTHENTICATION'
    ),
  };
  return categoryLabels[category] || category;
};

const getMediaLabel = format => {
  const mediaLabels = {
    IMAGE: t('WHATSAPP_TEMPLATES.PICKER.MEDIA_LABELS.IMAGE'),
    VIDEO: t('WHATSAPP_TEMPLATES.PICKER.MEDIA_LABELS.VIDEO'),
    DOCUMENT: t('WHATSAPP_TEMPLATES.PICKER.MEDIA_LABELS.DOCUMENT'),
  };
  return (
    mediaLabels[format] || t('WHATSAPP_TEMPLATES.PICKER.MEDIA_LABELS.FILE')
  );
};

const templateHasVariables = template =>
  (template.components || []).some(component =>
    /{{\s*\d+\s*}}/.test(component.text || '')
  );

const availableLanguages = computed(() =>
  [...new Set(whatsAppTemplateMessages.value.map(({ language }) => language))]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
);

const availableCategories = computed(() =>
  [...new Set(whatsAppTemplateMessages.value.map(({ category }) => category))]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
);

const categoryOptions = computed(() => [
  {
    value: '',
    label: t('WHATSAPP_TEMPLATES.PICKER.FILTERS.ALL_CATEGORIES'),
  },
  ...availableCategories.value.map(category => ({
    value: category,
    label: getCategoryLabel(category),
  })),
]);

const languageOptions = computed(() => [
  {
    value: '',
    label: t('WHATSAPP_TEMPLATES.PICKER.FILTERS.ALL_LANGUAGES'),
  },
  ...availableLanguages.value.map(language => ({
    value: language,
    label: language,
  })),
]);

const filteredTemplateMessages = computed(() => {
  const normalizedQuery = normalizeSearchText(query.value.trim());

  return whatsAppTemplateMessages.value
    .filter(template => {
      const matchesQuery =
        !normalizedQuery ||
        getTemplateSearchText(template).includes(normalizedQuery);
      const matchesLanguage =
        !selectedLanguage.value || template.language === selectedLanguage.value;
      const matchesCategory =
        !selectedCategory.value || template.category === selectedCategory.value;

      return matchesQuery && matchesLanguage && matchesCategory;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
});

const hasActiveFilters = computed(
  () => query.value || selectedLanguage.value || selectedCategory.value
);

watch(
  filteredTemplateMessages,
  templates => {
    if (!templates.length) {
      selectedTemplate.value = null;
      return;
    }

    const selectionIsVisible = templates.some(
      template => template.id === selectedTemplate.value?.id
    );
    if (!selectionIsVisible) selectedTemplate.value = templates[0];
  },
  { immediate: true }
);

const clearFilters = () => {
  query.value = '';
  selectedLanguage.value = '';
  selectedCategory.value = '';
};

const confirmSelection = () => {
  if (selectedTemplate.value) emit('onSelect', selectedTemplate.value);
};

const refreshTemplates = async () => {
  isRefreshing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inboxId);
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_SUCCESS'));
  } catch (error) {
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_ERROR'));
  } finally {
    isRefreshing.value = false;
  }
};
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col gap-4">
    <div class="flex shrink-0 items-center gap-2 rounded-xl bg-n-brand/5 p-1">
      <span class="template-step template-step--active">
        <span class="template-step__number">{{ pickerSteps.choose }}</span>
        {{ t('WHATSAPP_TEMPLATES.PICKER.CHOOSE_TITLE') }}
      </span>
      <span class="template-step">
        <span class="template-step__number">{{ pickerSteps.preview }}</span>
        {{ t('WHATSAPP_TEMPLATES.PICKER.PREVIEW_LABEL') }}
      </span>
    </div>
    <div class="flex flex-col gap-3 shrink-0">
      <div class="flex gap-2">
        <label
          class="flex h-11 flex-1 items-center gap-2 rounded-xl border border-n-weak bg-n-solid-2 px-3 shadow-inner focus-within:border-n-brand focus-within:ring-2 focus-within:ring-n-brand/10"
        >
          <fluent-icon icon="search" class="text-n-slate-11" size="18" />
          <input
            v-model="query"
            type="search"
            :placeholder="t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
            class="reset-base w-full bg-transparent text-n-slate-12 !text-sm !outline-0"
          />
        </label>
        <button
          type="button"
          :disabled="isRefreshing"
          class="flex shrink-0 gap-2 justify-center items-center px-3 h-11 rounded-xl text-sm font-medium text-n-slate-12 outline outline-1 outline-n-weak hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
          :title="t('WHATSAPP_TEMPLATES.PICKER.REFRESH_BUTTON')"
          @click="refreshTemplates"
        >
          <Icon
            icon="i-lucide-refresh-ccw"
            class="text-n-slate-12 size-4"
            :class="{ 'animate-spin': isRefreshing }"
          />
          <span class="hidden sm:inline">
            {{ t('WHATSAPP_TEMPLATES.PICKER.REFRESH_SHORT') }}
          </span>
        </button>
      </div>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1.5 min-w-0">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.PICKER.CATEGORY') }}
          </span>
          <Select
            v-model="selectedCategory"
            :options="categoryOptions"
            class="!w-full [&>select]:!w-full [&>select]:!h-10"
          />
        </label>
        <label class="flex flex-col gap-1.5 min-w-0">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.PICKER.LABELS.LANGUAGE') }}
          </span>
          <Select
            v-model="selectedLanguage"
            :options="languageOptions"
            class="!w-full [&>select]:!w-full [&>select]:!h-10"
          />
        </label>
      </div>

      <div class="flex gap-3 justify-end items-center min-h-5">
        <span class="text-xs text-n-slate-11">
          {{
            t('WHATSAPP_TEMPLATES.PICKER.FILTERS.RESULT_COUNT', {
              count: filteredTemplateMessages.length,
            })
          }}
        </span>
        <button
          v-if="hasActiveFilters"
          type="button"
          class="text-xs font-medium text-n-brand hover:underline"
          @click="clearFilters"
        >
          {{ t('WHATSAPP_TEMPLATES.PICKER.FILTERS.CLEAR') }}
        </button>
      </div>
    </div>

    <template v-if="filteredTemplateMessages.length">
      <div
        class="template-browser grid overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm"
      >
        <section class="template-list flex flex-col min-w-0 border-n-weak">
          <div class="px-4 py-3 border-b border-n-weak">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('WHATSAPP_TEMPLATES.PICKER.CHOOSE_TITLE') }}
            </h3>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ t('WHATSAPP_TEMPLATES.PICKER.CHOOSE_HINT') }}
            </p>
          </div>
          <div class="overflow-y-auto p-2 template-list-scroll">
            <button
              v-for="template in filteredTemplateMessages"
              :key="`${template.id}-${template.language}`"
              type="button"
              class="flex gap-3 items-start p-3 w-full text-left rounded-lg transition-colors hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              :class="{
                'bg-n-brand/10 outline outline-1 outline-n-brand':
                  selectedTemplate?.id === template.id,
              }"
              :aria-pressed="selectedTemplate?.id === template.id"
              @click="selectedTemplate = template"
            >
              <span
                class="flex shrink-0 justify-center items-center mt-0.5 size-5 rounded-full border"
                :class="
                  selectedTemplate?.id === template.id
                    ? 'border-n-brand bg-n-brand text-white'
                    : 'border-n-strong text-transparent'
                "
              >
                <Icon icon="i-lucide-check" class="size-3" />
              </span>
              <span class="min-w-0">
                <span class="block text-sm font-medium text-n-slate-12">
                  {{ formatTemplateName(template.name) }}
                </span>
                <span
                  class="template-summary block mt-1 text-xs text-n-slate-11"
                >
                  {{ getTemplateBody(template) }}
                </span>
                <span class="flex flex-wrap gap-1.5 items-center mt-2">
                  <span class="text-xs text-n-slate-10">
                    {{ getCategoryLabel(template.category) }}
                  </span>
                  <span
                    aria-hidden="true"
                    class="size-1 rounded-full bg-n-slate-7"
                  />
                  <span class="text-xs text-n-slate-10">
                    {{ template.language }}
                  </span>
                </span>
              </span>
            </button>
          </div>
        </section>

        <section
          v-if="selectedTemplate"
          class="template-detail flex min-w-0 max-w-full flex-col bg-n-brand/5"
        >
          <div
            class="flex shrink-0 justify-between gap-3 items-start px-5 py-4 border-b border-n-weak"
          >
            <div class="min-w-0">
              <p class="text-xs font-medium text-n-slate-10">
                {{ t('WHATSAPP_TEMPLATES.PICKER.PREVIEW_LABEL') }}
              </p>
              <h3 class="mt-1 text-base font-semibold text-n-slate-12">
                {{ formatTemplateName(selectedTemplate.name) }}
              </h3>
              <p class="mt-1 text-xs text-n-slate-10 break-all">
                {{
                  t('WHATSAPP_TEMPLATES.PICKER.INTERNAL_NAME', {
                    name: selectedTemplate.name,
                  })
                }}
              </p>
            </div>
            <span
              class="overflow-hidden shrink-0 max-w-32 px-2.5 py-1 text-xs text-ellipsis whitespace-nowrap rounded-full bg-n-slate-3 text-n-slate-11"
              :title="getCategoryLabel(selectedTemplate.category)"
            >
              {{ getCategoryLabel(selectedTemplate.category) }}
            </span>
          </div>

          <div class="overflow-y-auto flex-1 p-5 template-preview-scroll">
            <div class="mx-auto max-w-lg">
              <p class="mb-2 text-xs text-center text-n-slate-10">
                {{ t('WHATSAPP_TEMPLATES.PICKER.PREVIEW_HINT') }}
              </p>
              <article
                class="overflow-hidden max-w-full rounded-2xl border border-n-weak bg-n-solid-1 p-4 text-n-slate-12 shadow-md break-words"
              >
                <div
                  v-if="hasMediaContent(selectedTemplate)"
                  class="flex gap-2 justify-center items-center mb-3 h-28 rounded-lg bg-n-alpha-black2 text-n-slate-10"
                >
                  <Icon icon="i-lucide-image" class="size-5" />
                  <span class="text-sm">
                    {{
                      getMediaLabel(getTemplateHeader(selectedTemplate).format)
                    }}
                  </span>
                </div>
                <p
                  v-else-if="getTemplateHeader(selectedTemplate)?.text"
                  class="mb-2 font-semibold whitespace-pre-wrap"
                >
                  {{ getTemplateHeader(selectedTemplate).text }}
                </p>
                <p class="text-sm leading-6 whitespace-pre-wrap">
                  {{ getTemplateBody(selectedTemplate) }}
                </p>
                <p
                  v-if="getTemplateFooter(selectedTemplate)"
                  class="mt-2 text-xs text-n-slate-10 whitespace-pre-wrap"
                >
                  {{ getTemplateFooter(selectedTemplate) }}
                </p>
                <div
                  v-if="getTemplateButtons(selectedTemplate).length"
                  class="pt-3 mt-3 border-t border-n-weak"
                >
                  <div
                    v-for="button in getTemplateButtons(selectedTemplate)"
                    :key="`${button.type}-${button.text}`"
                    class="py-1.5 text-sm font-medium text-center text-n-brand"
                  >
                    {{ button.text }}
                  </div>
                </div>
              </article>
              <div
                v-if="templateHasVariables(selectedTemplate)"
                class="flex gap-2 items-start p-3 mt-3 rounded-lg bg-n-brand/10 text-n-slate-11"
              >
                <Icon
                  icon="i-lucide-info"
                  class="shrink-0 mt-0.5 size-4 text-n-brand"
                />
                <p class="text-xs leading-5">
                  {{ t('WHATSAPP_TEMPLATES.PICKER.VARIABLES_HINT') }}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <footer
        data-testid="template-picker-actions"
        class="flex shrink-0 items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 shadow-sm"
      >
        <p class="mb-0 hidden text-xs text-n-slate-10 sm:block">
          {{ t('WHATSAPP_TEMPLATES.PICKER.CONFIRM_HINT') }}
        </p>
        <NextButton
          type="button"
          icon="i-lucide-arrow-right"
          class="ml-auto max-w-full"
          :label="t('WHATSAPP_TEMPLATES.PICKER.USE_TEMPLATE')"
          @click="confirmSelection"
        />
      </footer>
    </template>

    <div v-else class="py-12 text-center rounded-xl border border-n-weak">
      <div v-if="hasActiveFilters && whatsAppTemplateMessages.length">
        <Icon
          icon="i-lucide-search-x"
          class="mx-auto mb-3 size-7 text-n-slate-10"
        />
        <p class="font-medium text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATES.PICKER.NO_RESULTS_TITLE') }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
        </p>
        <button
          type="button"
          class="mt-3 text-sm font-medium text-n-brand hover:underline"
          @click="clearFilters"
        >
          {{ t('WHATSAPP_TEMPLATES.PICKER.FILTERS.CLEAR') }}
        </button>
      </div>
      <div v-else>
        <p class="text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_AVAILABLE') }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.template-step {
  @apply flex h-8 flex-1 items-center justify-center gap-2 rounded-lg text-xs font-medium text-n-slate-10;
}

.template-step--active {
  @apply bg-n-brand font-semibold text-white shadow-sm;
}

.template-step__number {
  @apply grid size-5 place-items-center rounded-full bg-n-slate-4;
}

.template-step--active .template-step__number {
  @apply bg-white/20;
}
.template-browser {
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  flex: 1;
  width: 100%;
  min-height: 0;
}

.template-list,
.template-detail {
  width: 100%;
  min-height: 0;
  max-width: 100%;
  overflow: hidden;
}

.template-list {
  border-right-width: 1px;
}

.template-list-scroll,
.template-preview-scroll {
  flex: 1;
  min-height: 0;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.template-summary {
  display: -webkit-box;
  overflow: hidden;
  white-space: pre-line;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

@media (max-width: 48rem) {
  .template-browser {
    grid-template-columns: 1fr;
    display: block;
    overflow-y: auto;
  }

  .template-list {
    border-right-width: 0;
    border-bottom-width: 1px;
  }

  .template-list-scroll {
    flex: none;
    max-height: 16rem;
  }

  .template-preview-scroll {
    flex: none;
    max-height: 22rem;
  }
}
</style>
